import { Filter, GlProgram, UniformGroup } from "pixi.js";

const vertex = `in vec2 aPosition;
out vec2 vTextureCoord;
uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;
vec4 filterVertexPosition(void) {
  vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
  position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}
vec2 filterTextureCoord(void) { return aPosition * (uOutputFrame.zw * uInputSize.zw); }
void main(void) { gl_Position = filterVertexPosition(); vTextureCoord = filterTextureCoord(); }`;

/**
 * HD-2D post in one pass (the mocks' numpy pipeline on the GPU):
 * tilt-shift blur on the far and near edges, bloom from bright pixels, soft highlight roll-off,
 * warm-highlight / teal-shadow grade with a touch of saturation, vignette and faint grain.
 */
const fragment = `in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform highp vec4 uInputSize;
uniform highp vec4 uOutputFrame;
uniform highp float uTime;
uniform float uTiltTop;
uniform float uTiltBottom;
uniform float uBloom;
uniform float uGrain;
uniform float uVignette;

vec3 tap(vec2 uv) { return texture(uTexture, uv).rgb; }

void main() {
  vec2 uv = vTextureCoord;
  vec2 px = uInputSize.zw;
  // screen-space y in 0..1 over the visible frame
  float sy = (uv.y / (uOutputFrame.w * uInputSize.w));
  vec3 base = tap(uv);

  // tilt-shift: blur grows towards the top and bottom bands
  float wTop = clamp((uTiltTop - sy) / uTiltTop, 0.0, 1.0);
  float wBot = clamp((sy - uTiltBottom) / (1.0 - uTiltBottom), 0.0, 1.0);
  float tilt = max(wTop, wBot);
  vec3 col = base;
  if (tilt > 0.01) {
    vec3 acc = vec3(0.0);
    float r = 1.0 + tilt * 3.0;
    for (int i = -2; i <= 2; i++) for (int j = -2; j <= 2; j++) acc += tap(uv + vec2(float(i), float(j)) * px * r);
    col = mix(base, acc / 25.0, tilt * 0.7);
  }

  // bloom: gather bright neighbours on a ring
  if (uBloom > 0.0) {
    vec3 glow = vec3(0.0);
    for (int k = 0; k < 12; k++) {
      float a = float(k) * 0.5236;
      for (int s = 1; s <= 3; s++) {
        vec3 c = tap(uv + vec2(cos(a), sin(a)) * px * float(s) * 9.0);
        float l = dot(c, vec3(0.299, 0.587, 0.114));
        glow += c * clamp((l - 0.86) / 0.14, 0.0, 1.0);
      }
    }
    col += glow / 36.0 * uBloom;
  }

  // soft roll-off of highlights
  col = mix(col, 0.82 + (1.0 - exp(-(col - 0.82) / 0.18)) * 0.18, step(0.82, col));

  // grade: teal shadows, warm highlights, a little more saturation
  float lum = clamp(dot(col, vec3(0.299, 0.587, 0.114)), 0.0, 1.0);
  col += (1.0 - lum) * (1.0 - lum) * vec3(-0.015, 0.02, 0.04) + lum * lum * vec3(0.03, 0.012, -0.02);
  col = vec3(lum) + (col - vec3(lum)) * 1.1;

  // vignette
  vec2 q = vec2(uv.x / (uOutputFrame.z * uInputSize.z), sy) - 0.5;
  float rr = length(q * 2.0) / 1.4142;
  col *= 1.0 - uVignette * pow(rr, 2.4);

  // grain
  float n = fract(sin(dot(gl_FragCoord.xy + uTime, vec2(12.9898, 78.233))) * 43758.5453) - 0.5;
  col += n * uGrain;

  finalColor = vec4(col, 1.0);
}`;

export class HD2DFilter extends Filter {
  constructor() {
    super({
      glProgram: GlProgram.from({ vertex, fragment, name: "hd2d-post" }),
      resources: {
        hd2d: new UniformGroup({
          uTime: { value: 0, type: "f32" },
          uTiltTop: { value: 0.16, type: "f32" },
          uTiltBottom: { value: 0.94, type: "f32" },
          uBloom: { value: 0.55, type: "f32" },
          uGrain: { value: 0.018, type: "f32" },
          uVignette: { value: 0.16, type: "f32" },
        }),
      },
    });
  }

  get u() {
    return this.resources.hd2d.uniforms as { uTime: number; uTiltTop: number; uTiltBottom: number; uBloom: number; uGrain: number; uVignette: number };
  }
}
